import { useQuery } from "@tanstack/react-query";
import { api } from "../api";
import type {
  Phase1StatusResponse,
  SemPhaseStatus,
} from "../api-types";

const POLL_INTERVAL_MS = 3000;

const TERMINAL_STATUSES: ReadonlyArray<SemPhaseStatus> = ["ready", "failed"];

/**
 * Polls `GET /sem-provisioning/phase-1-status/:userId` while the customer
 * is on any screen that consumes the GA4/GTM IDs or status. Polling stops
 * once `phase1Status` is terminal (`ready` or `failed`).
 *
 * Keeps polling on `null`, `"idle"`, and `"running"` because the backend
 * doesn't fire its atomic claim until the user saves on the Competitors
 * step — the spinner still needs to spin in the meantime.
 *
 * Use `enabled` to gate the polling to mounted-component lifetime.
 */
export function usePhase1Status(
  userId: string | undefined,
  enabled: boolean,
) {
  return useQuery<Phase1StatusResponse>({
    queryKey: ["phase1-status", userId],
    queryFn: () => {
      if (!userId) {
        throw new Error("User ID is required");
      }
      return api.getPhase1Status(userId);
    },
    enabled: !!userId && enabled,
    refetchInterval: (data) =>
      data && TERMINAL_STATUSES.includes(data.phase1Status)
        ? false
        : POLL_INTERVAL_MS,
    staleTime: 0,
    refetchOnWindowFocus: false,
  });
}
